import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertTriangle, RefreshCw, Home, Bug } from "lucide-react";
import { logClientError } from "@/lib/errorLogger";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class PageErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("PageErrorBoundary caught error:", {
      error,
      errorInfo,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });

    void logClientError(error, "PageErrorBoundary", { componentStack: errorInfo.componentStack });


    this.setState({
      error,
      errorInfo
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  public render() {
    if (this.state.hasError) {
      const { fallbackTitle, fallbackMessage } = this.props;

      return (
        <div className="min-h-screen bg-gradient-to-b from-cyan-light/30 to-background flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full p-8 space-y-6">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-10 h-10 text-destructive" />
              </div>
            </div>

            {/* Title & Message */}
            <div className="text-center space-y-3">
              <h1 className="text-3xl font-heading font-black text-foreground">
                {fallbackTitle || "Oeps! Er ging iets mis 😅"}
              </h1>
              <p className="text-lg text-muted-foreground max-w-md mx-auto">
                {fallbackMessage || 
                  "Deze pagina kon niet worden geladen. Geen zorgen, je data is veilig!"}
              </p>
            </div>

            {/* Error Details (only in dev) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="bg-muted/50 rounded-lg p-4 text-left">
                <summary className="cursor-pointer font-semibold text-sm mb-2 flex items-center gap-2">
                  <Bug className="w-4 h-4" />
                  Technische details (alleen zichtbaar in development)
                </summary>
                <div className="mt-3 space-y-2">
                  <p className="text-xs font-mono text-destructive break-all">
                    <strong>Error:</strong> {this.state.error.message}
                  </p>
                  {this.state.error.stack && (
                    <pre className="text-xs font-mono text-foreground/70 overflow-auto max-h-40 bg-background/50 p-2 rounded">
                      {this.state.error.stack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
              <Button
                onClick={this.handleReset}
                variant="default"
                size="lg"
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Probeer opnieuw
              </Button>
              <Button
                onClick={() => window.location.href = '/'}
                variant="outline"
                size="lg"
                className="gap-2"
              >
                <Home className="w-4 h-4" />
                Terug naar home
              </Button>
            </div>

            {/* Help text */}
            <p className="text-sm text-center text-muted-foreground pt-4">
              Als het probleem blijft bestaan, neem dan contact op met support via{" "}
              <a href="/support" className="text-primary hover:underline font-medium">
                ons contactformulier
              </a>
            </p>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
